// Session history sidebar for AskPsdPageV2 — lists past conversations
// grouped by recency (Today/Yesterday/N days ago), same visual language as
// the rest of v2 (pill buttons, muted metadata, subtle borders).
import React from 'react';
import { Icon } from 'pepsico-ds';

function formatGroupLabel(iso) {
  if (!iso) {
    return 'Earlier';
  }
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return 'Earlier';
  }
  const diffDays = Math.floor((Date.now() - date.getTime()) / 86400000);
  if (diffDays <= 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function groupSessions(sessions) {
  const groups = new Map();
  sessions.forEach((session) => {
    const label = formatGroupLabel(session.updatedAt);
    if (!groups.has(label)) {
      groups.set(label, []);
    }
    groups.get(label).push(session);
  });
  return Array.from(groups.entries());
}

export default function SessionSidebar({
  sessions,
  activeSessionId,
  onResume,
  onDelete,
  onNew,
  loading,
  error,
}) {
  const groupedSessions = groupSessions(sessions);

  return (
    <aside className="ask-psd-v2-session-sidebar">
      <div className="ask-psd-v2-session-sidebar-header">
        <span>History</span>
        <button type="button" className="ask-psd-v2-session-new" onClick={onNew}>
          + New
        </button>
      </div>

      {loading && <p className="ask-psd-v2-session-empty">Loading sessions…</p>}

      {error && !loading && <p className="ask-psd-v2-session-empty ask-psd-v2-session-error">{error}</p>}

      {!loading && !error && sessions.length === 0 && (
        <p className="ask-psd-v2-session-empty">No sessions yet. Ask a question to start.</p>
      )}

      {!loading && sessions.length > 0 && (
        <div className="ask-psd-v2-session-list">
          {groupedSessions.map(([label, group]) => (
            <div key={label} className="ask-psd-v2-session-group">
              <p className="ask-psd-v2-session-group-label">{label}</p>
              {group.map((session) => (
                <div
                  key={session.sessionId}
                  className={`ask-psd-v2-session-item${
                    session.sessionId === activeSessionId ? ' is-active' : ''
                  }`}
                  onClick={() => onResume(session.sessionId)}
                  title={session.title || session.lastQuestion || session.sessionId}
                >
                  <div className="ask-psd-v2-session-item-body">
                    <span className="ask-psd-v2-session-item-title">
                      {session.title || session.lastQuestion || 'Untitled session'}
                    </span>
                    {session.turnCount > 0 && (
                      <span className="ask-psd-v2-session-item-meta">
                        {session.turnCount} turn{session.turnCount !== 1 ? 's' : ''}
                      </span>
                    )}
                  </div>
                  <button
                    type="button"
                    className="ask-psd-v2-session-delete"
                    title="Delete session"
                    aria-label="Delete session"
                    onClick={(event) => {
                      event.stopPropagation();
                      onDelete(session.sessionId);
                    }}
                  >
                    <Icon icon="delete" size="small" />
                  </button>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
    </aside>
  );
}
